import {useContext} from 'react';
import Swal from 'sweetalert2';
import {useNavigate, useParams} from 'react-router-dom';
import {doc, getFirestore, writeBatch} from 'firebase/firestore';
import {Card} from 'react-bootstrap';
import {LandingNav} from 'landing/components/LandingNav';
import {FormixFormWithData} from 'components/Forms/FormixFormWithData';
import {ErrorPage} from 'components/errors/ErrorPage';
import {Loading} from 'components/loading/Loading';
import {ANONYMOUS_DISPLAY_NAME, ProfileContext} from 'contexts/ProfileContext';
import {
  generateAboutFormDefaultValues,
  generateAboutFormFormat,
} from './AboutFormInputsDynamic';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {type ApplicationType} from '../firebase/models/applicationData';
import {
  getAppSplashRoute,
  useApplicationType,
  useIsApplyShortLinkRoute,
} from '../general/utils/apputils';
import {getUserId} from 'hooks/user/useUserId';
import {
  type FormInfo,
  GENDER_OPTIONS,
  GenderQuestionId,
  GeolocationQuestionId,
  NameQuestionId,
  getOrderedFormFields,
} from '../firebase/models/formData';
import {useApplicationJoinData} from '../layout/ApplicationJoinLayout';
import {isNullOrUndefined} from 'utils/general';

// Required as naming conventions are not aligned in Landing Nav
const APP_TYPE_TO_NAME = {
  sl: 'sectionLeader',
  student: 'student',
};

export const CommonAboutForm = () => {
  // Resolved from the URL (not route params): the shared detector recognizes
  // both the long-form and `/apply/…` short-link shapes, so the form saves to
  // the correct applicant document under either. Defaults to 'student' only
  // for unreachable non-application URLs.
  const applicationType: ApplicationType = useApplicationType() ?? 'student';

  return (
    <div>
      <LandingNav
        applicationType={APP_TYPE_TO_NAME[applicationType]}
        subpage={'About You'}
      />
      <div style={outerStyle}>
        <AboutInner applicationType={applicationType} />
      </div>
    </div>
  );
};

const AboutInner = ({applicationType}: {applicationType: ApplicationType}) => {
  const {courseId, targetCourseId} = useParams();
  const navigate = useNavigate();

  const {userData, userProtectedData, loading: profileLoading} =
    useContext(ProfileContext);
  const t = useTranslateFunction('application');

  // Preserve the URL shape the user is on (short `/apply/…` vs long form) so
  // going back swaps the layout's <Outlet/> instead of remounting the flow.
  const isShortLinkRoute = useIsApplyShortLinkRoute();
  const backUrl = getAppSplashRoute(
    targetCourseId,
    applicationType,
    courseId,
    isShortLinkRoute,
  );

  // The application structure and the applicant's saved answers are loaded once
  // by the parent ApplicationJoinLayout and kept mounted across navigation, so
  // hopping between this page and the splash page does not re-fetch them.
  const {
    appStructureData,
    appStructureLoading,
    appStructureError,
    applicantData,
    applicantDataLoading,
    applicantDataError,
    applicantDataRef,
  } = useApplicationJoinData();

  if (profileLoading || appStructureLoading || applicantDataLoading) {
    return <Loading />;
  }

  if (applicantDataError || appStructureError) {
    return (
      <ErrorPage
        msg={t('Error loading data from server. Please refresh the page.')}
      />
    );
  }

  const defaultData = generateAboutFormDefaultValues(
    getOrderedFormFields(appStructureData?.formInfo),
  );

  const aboutData = applicantData ?? defaultData;

  safeInject(aboutData, userProtectedData, 'dateOfBirth');
  // Older profiles store legacy slugs (e.g. 'female'), which match no option.
  if (GENDER_OPTIONS.includes(userProtectedData?.gender)) {
    safeInject(aboutData, userProtectedData, 'gender');
  }

  const setAboutData = function (newFields) {
    const userId = getUserId();
    if (!userId) return;

    // Legal name and gender are private, so they go on the protected doc. Only
    // fields the form collected are written: an absent field is `undefined`,
    // which Firestore rejects outright.
    const name = newFields[NameQuestionId];
    const gender = newFields[GenderQuestionId];
    const protectedUpdate: {
      legalName?: string;
      gender?: string;
    } = {};
    if (!isNullOrUndefined(name) && name !== '') {
      protectedUpdate.legalName = name;
    }
    if (!isNullOrUndefined(gender) && gender !== '') {
      protectedUpdate.gender = gender;
    }

    // Public user doc: country, plus a display name seeded from the given name
    // only when they lack a real one ('Anon'/empty count as missing, matching
    // ProfileContext).
    const hasDisplayName =
      !isNullOrUndefined(userData?.displayName) &&
      userData.displayName.length > 0 &&
      userData.displayName !== ANONYMOUS_DISPLAY_NAME;
    const country = newFields[GeolocationQuestionId]?.country;
    const publicUpdate: {displayName?: string; country?: string} = {};
    if (!hasDisplayName && !isNullOrUndefined(name) && name !== '') {
      publicUpdate.displayName = name;
    }
    if (!isNullOrUndefined(country) && country !== '') {
      publicUpdate.country = country;
    }

    // Commit the application doc and the profile mirror in one atomic batch, so
    // a failure can't leave the profile half-written relative to the
    // application.
    const db = getFirestore();
    const batch = writeBatch(db);
    batch.set(applicantDataRef, {...newFields}, {merge: true});
    if (Object.keys(protectedUpdate).length > 0) {
      batch.set(doc(db, `users/${userId}/docs/protected`), protectedUpdate, {
        merge: true,
      });
    }
    if (Object.keys(publicUpdate).length > 0) {
      batch.set(doc(db, `users/${userId}`), publicUpdate, {merge: true});
    }

    batch
      .commit()
      .then(() => {
        Swal.fire({
          title: t('Success') as string,
          heightAuto: false,
          text: t('Your form is filled out!') as string,
          icon: 'success',
          preConfirm: () => {
            navigate(backUrl);
          },
        });
      })
      .catch((error: unknown) => {
        console.error('Failed to save About form', error);
        Swal.fire({
          title: t('Error') as string,
          heightAuto: false,
          text: t(
            'Something went wrong saving your form. Please try again.',
          ) as string,
          icon: 'error',
        });
      });
  };

  return (
    <AboutWithData
      aboutData={aboutData}
      setAboutData={setAboutData}
      formData={appStructureData?.formInfo}
      backUrl={backUrl}
    />
  );
};

const AboutWithData = ({
  aboutData,
  setAboutData,
  formData,
  backUrl,
}: {
  aboutData: Record<string, unknown>;
  setAboutData: (fields: Record<string, unknown>) => void;
  formData?: FormInfo;
  backUrl: string;
}) => {
  const t = useTranslateFunction('application');
  const applicationFormStructure = generateAboutFormFormat(
    getOrderedFormFields(formData),
    t,
  );

  return (
    <>
      <Card style={{margin: 'auto', padding: '20px'}}>
        <div>
          <FormixFormWithData
            serverData={aboutData}
            backUrl={backUrl}
            formFormat={applicationFormStructure}
            onSubmit={(e) => setAboutData(e)}
            // Uniform field spacing for the About form; see `.form-field-stack`
            // in styles/formComponents.scss.
            className="form-field-stack"
          />
        </div>
      </Card>
    </>
  );
};

function safeInject(serverData, fromData, key) {
  if (!serverData || !fromData) {
    return;
  }
  if (key in serverData) {
    return;
  }
  if (!(key in fromData)) {
    return;
  }
  serverData[key] = fromData[key];
}

const outerStyle = {
  width: '720px',
  maxWidth: '90vw',
  margin: 'auto',
  marginTop: '50px',
  marginBottom: '50px',
};
